import AutoStoriesOutlined from '@mui/icons-material/AutoStoriesOutlined';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import RouteOutlined from '@mui/icons-material/RouteOutlined';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  Stack,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useShallow } from 'zustand/react/shallow';

import {
  getOnboardingHeading,
  getReturningItems,
  getOnboardingSteps,
  getOnboardingSummary,
  getWhatsNewItems,
  OnboardingExperience,
  OnboardingReturnBand,
  OnboardingMode
} from '@/components/onboarding/onboardingContent';
import { usePutData } from '@/helpers/hooks';
import { useGlobalSettingsStore } from '@/store/useGlobalSettingsStore';

interface IProps {
  experience: OnboardingExperience;
  returnBand: OnboardingReturnBand;
  daysAway: number | null;
  nextRoute: string;
  isStrategyOwner: boolean;
}
const WelcomeOnboardingTour = ({ experience, returnBand, daysAway, nextRoute, isStrategyOwner }: IProps) => {
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [tourMode, setTourMode] = useState<OnboardingMode>('quick');

  const { isReadOnlyImpersonation, isSharedEngagement } = useGlobalSettingsStore(
    useShallow((state) => ({
      isReadOnlyImpersonation: state.globalSettings.isReadOnlyImpersonation,
      isSharedEngagement: state.globalSettings.isSocialFeedEnabled
    }))
  );
  const tourSteps = useMemo<Step[]>(
    () => getOnboardingSteps(window.location.pathname, tourMode, { isSharedEngagement }),
    [isSharedEngagement, tourMode]
  );

  const updateOnboardingMutation = usePutData<unknown, unknown>('/user/updateonboardingdate');

  useEffect(() => {
    const isPersonalisationSetup =
      window.location.pathname === '/profile' && new URLSearchParams(window.location.search).get('onboarding') === 'setup';

    if (isReadOnlyImpersonation || isPersonalisationSetup) {
      setShowWelcomeModal(false);
      setRunTour(false);
      return;
    }

    if (experience !== 'none' && !isReadOnlyImpersonation) {
      setShowWelcomeModal(true);
    }
  }, [experience, isReadOnlyImpersonation]);

  const handleStartTour = (mode: OnboardingMode) => {
    setTourMode(mode);
    setShowWelcomeModal(false);

    setTimeout(() => {
      setRunTour(true);
    }, 500);
  };

  const handleCompleteOnboarding = async () => {
    setShowWelcomeModal(false);
    await updateOnboardingMutation.mutateAsync();
  };

  const handlePersonalise = () => {
    const parameters = new URLSearchParams({ onboarding: 'setup', next: nextRoute });
    window.location.assign(`/profile?${parameters.toString()}`);
  };

  const handleJoyrideCallback = useCallback(
    async (data: CallBackProps) => {
      const { status } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setRunTour(false);
        await updateOnboardingMutation.mutateAsync();
      }
    },
    [updateOnboardingMutation]
  );

  return (
    <>
      <Dialog
        open={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 2
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h4" component="div" fontWeight="bold" color="primary">
            {getOnboardingHeading(experience)}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">{getOnboardingSummary(experience, returnBand, daysAway)}</Typography>
          {experience === 'new' && (
            <>
              <Grid container spacing={1.5} sx={{ mt: 2 }}>
                {[
                  {
                    icon: <AccountCircleOutlined color="primary" />,
                    title: 'Make it yours',
                    body: 'Add your name, mobile and profile picture.'
                  },
                  {
                    icon: <NotificationsActiveOutlined color="primary" />,
                    title: 'Choose your rhythm',
                    body: 'Set when reminders begin and how often they repeat.'
                  },
                  {
                    icon: <RouteOutlined color="primary" />,
                    title: 'Start in the right place',
                    body: isStrategyOwner
                      ? 'Continue into the Strategy creation wizard.'
                      : 'Continue to the work assigned to you.'
                  }
                ].map((item) => (
                  <Grid size={{ xs: 12, sm: 4 }} key={item.title}>
                    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, bgcolor: '#FCFAF7' }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        {item.icon}
                        <Typography fontWeight={700} sx={{ mt: 0.75 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.body}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                You can change these choices later from your Profile.
              </Typography>
            </>
          )}
          {experience === 'returning' && (
            <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, mt: 1.5, color: 'text.secondary' }}>
              {getReturningItems(returnBand, isSharedEngagement).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </Stack>
          )}
          {experience === 'whatsNew' && (
            <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, mt: 1.5, color: 'text.secondary' }}>
              {getWhatsNewItems(isSharedEngagement).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: 3, paddingTop: 1, flexWrap: 'wrap' }}>
          <Button onClick={() => setShowWelcomeModal(false)} color="inherit">
            Not now
          </Button>
          <Button onClick={handleCompleteOnboarding} color="inherit">
            Don’t show again
          </Button>
          {experience === 'new' ? (
            <Button onClick={handlePersonalise} variant="contained" color="primary" autoFocus>
              Personalise my weavr
            </Button>
          ) : (
            <Button onClick={() => handleStartTour('quick')} variant="outlined" color="primary">
              Quick refresher
            </Button>
          )}
          <Button
            startIcon={<AutoStoriesOutlined />}
            onClick={() => handleStartTour('learn')}
            variant="contained"
            color="primary"
            autoFocus={experience !== 'new'}
          >
            Self-learning tutorial
          </Button>
        </DialogActions>
      </Dialog>

      {runTour && (
        <Joyride
          steps={tourSteps}
          run={runTour}
          continuous
          showProgress
          showSkipButton
          callback={handleJoyrideCallback}
          styles={{
            options: {
              primaryColor: '#9C5738',
              zIndex: 10000
            },
            tooltip: {
              borderRadius: 8,
              fontSize: 16
            },
            buttonNext: {
              backgroundColor: '#9C5738',
              borderRadius: 4,
              padding: '8px 16px'
            },
            buttonBack: {
              color: '#9C5738',
              marginRight: 10
            },
            buttonSkip: {
              color: '#666'
            }
          }}
          locale={{
            back: 'Back',
            close: 'Close',
            last: 'Finish',
            next: 'Next',
            skip: 'Finish later'
          }}
        />
      )}
    </>
  );
};

export default WelcomeOnboardingTour;
